import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  targetRect: DOMRect | null;
  content: string;
}

const Tooltip = ({ targetRect, content }: TooltipProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [opacity, setOpacity] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [arrowPosition, setArrowPosition] = useState(50); // Arrow position as percentage
  const [arrowDirection, setArrowDirection] = useState<"up" | "down">("up");
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (targetRect) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tooltipMinWidth = 180;
      const tooltipMaxWidth = 320;
      const tooltipHeight = 50; // Estimated height including padding
      const padding = 12; // Padding from viewport edges
      const arrowHeight = 8;
      const verticalOffset = 8; // Space between tooltip and element

      // Calculate initial position (centered above element)
      let left = targetRect.left + targetRect.width / 2 + window.scrollX;
      let top =  targetRect.top - tooltipHeight - arrowHeight - verticalOffset + window.scrollY;

      // Estimate tooltip width (will be refined after render)
      const estimatedWidth = Math.min(Math.max(tooltipMinWidth, content.length * 7), tooltipMaxWidth);
      const tooltipHalfWidth = estimatedWidth / 2;

      // Adjust horizontal position to stay within viewport
      if (left - tooltipHalfWidth < padding + window.scrollX) {
        // Too far left - align to left edge with padding
        left = padding + tooltipHalfWidth + window.scrollX;
        // Adjust arrow position to point to target
        const targetCenter = targetRect.left + targetRect.width / 2 + window.scrollX;
        const arrowOffset = ((targetCenter - left) / tooltipHalfWidth) * 50;
        setArrowPosition(60 + arrowOffset);
      } else if (left + tooltipHalfWidth > viewportWidth - padding + window.scrollX) {
        // Too far right - align to right edge with padding
        left = viewportWidth - padding - tooltipHalfWidth + window.scrollX;
        // Adjust arrow position to point to target
        const targetCenter = targetRect.left + targetRect.width / 2 + window.scrollX;
        const arrowOffset = ((targetCenter - left) / tooltipHalfWidth) * 50;
        setArrowPosition(50 + arrowOffset);
      } else {
        // Centered - arrow stays centered
        setArrowPosition(50);
      }

      // Adjust vertical position to stay within viewport
      const spaceAbove = targetRect.top - tooltipHeight - arrowHeight - verticalOffset;
      const spaceBelow = viewportHeight - targetRect.bottom - tooltipHeight - arrowHeight - verticalOffset;
      
      if (spaceAbove < padding && spaceBelow >= padding) {
        // Not enough space above, but enough below - place below element
        top = targetRect.bottom + arrowHeight + verticalOffset + window.scrollY;
        setArrowDirection("down");
      } else {
        // Place above (default)
        setArrowDirection("up");
        // Check if bottom would overflow
        if (top + tooltipHeight > window.scrollY + viewportHeight - padding) {
          top = window.scrollY + viewportHeight - tooltipHeight - padding;
        }
      }

      setPosition({ top, left });
      setIsVisible(true);
      // Trigger fade in
      requestAnimationFrame(() => {
        setOpacity(1);
      });
    } else {
      // Trigger fade out
      setOpacity(0);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [targetRect, content]);

  // Refine positioning after render with actual tooltip dimensions
  useEffect(() => {
    if (!tooltipRef.current || !targetRect || !isVisible || opacity !== 1) return;

    // Small delay to ensure tooltip is fully rendered and measured
    const timeoutId = setTimeout(() => {
      if (!tooltipRef.current || !targetRect) return;
      
      setPosition((currentPosition) => {
        const tooltipRect = tooltipRef.current!.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 12;

        let { top, left } = currentPosition;
        let adjusted = false;

        // Refine horizontal positioning
        const tooltipHalfWidth = tooltipRect.width / 2;
        const tooltipLeftEdge = left - tooltipHalfWidth;
        const tooltipRightEdge = left + tooltipHalfWidth;
        
        if (tooltipLeftEdge < padding + window.scrollX) {
          left = padding + tooltipHalfWidth + window.scrollX;
          adjusted = true;
        } else if (tooltipRightEdge > viewportWidth - padding + window.scrollX) {
          left = viewportWidth - padding - tooltipHalfWidth + window.scrollX;
          adjusted = true;
        }

        // Refine vertical positioning
        if (top < window.scrollY + padding) {
          top = window.scrollY + padding;
          adjusted = true;
        } else if (top + tooltipRect.height > window.scrollY + viewportHeight - padding) {
          top = window.scrollY + viewportHeight - tooltipRect.height - padding;
          adjusted = true;
        }

        // Update arrow position if tooltip was adjusted horizontally
        if (adjusted && targetRect) {
          const targetCenter = targetRect.left + targetRect.width / 2 + window.scrollX;
          const arrowOffset = ((targetCenter - left) / tooltipHalfWidth) * 50;
          const newArrowPos = Math.max(10, Math.min(90, 50 + arrowOffset)); // Clamp between 10% and 90%
          setArrowPosition(newArrowPos);
        }

        return adjusted ? { top, left } : currentPosition;
      });
    }, 10);

    return () => clearTimeout(timeoutId);
  }, [targetRect, isVisible, opacity]);

  if (!targetRect || !isVisible) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        transform: "translateX(-50%)",
        background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
        color: "#fff",
        padding: "12px 16px",
        borderRadius: "14px",
        border: "1px solid rgba(148, 163, 184, 0.35)",
        fontSize: "12px",
        fontWeight: 500,
        whiteSpace: "pre-line",
        zIndex: 9999,
        lineHeight: 1.5,
        minWidth: "200px",
        maxWidth: "340px",
        textAlign: "left",
        pointerEvents: "none",
        boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
        letterSpacing: "0.025em",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        opacity: opacity,
        transition: "opacity 0.18s ease-out",
        backdropFilter: "blur(12px)",
      }}
    >
      {content}
      {/* Arrow */}
      <div
        style={{
          position: "absolute",
          bottom: arrowDirection === "up" ? "-8px" : undefined,
          top: arrowDirection === "down" ? "-8px" : undefined,
          left: `${arrowPosition}%`,
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "8px solid transparent",
          borderRight: "8px solid transparent",
          borderTop: arrowDirection === "up" ? "8px solid #1f2937" : "none",
          borderBottom: arrowDirection === "down" ? "8px solid #1f2937" : "none",
        }}
      />
    </div>,
    document.body
  );
};

export default Tooltip;
